import { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';
import { AutomationConfig } from '@/features/sales/types/config';

export function useSxmAutomationConfig(userId?: string) {
    const [config, setConfig] = useState<AutomationConfig | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);

    const fetchConfig = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get<AutomationConfig>('/sxm/config', {
                params: { userId }
            });
            setConfig(response.data);
        } catch (err: unknown) {
            console.error('Failed to fetch automation config:', err);
            setError(err instanceof Error ? err : new Error(getErrorMessage(err)));
        } finally {
            setLoading(false);
        }
    }, [userId]);

    const updateConfig = async (newConfig: AutomationConfig) => {
        setIsUpdating(true);
        try {
            const response = await api.put<AutomationConfig>('/sxm/config', {
                ...newConfig,
                userId
            });
            setConfig(response.data);
            return response.data;
        } catch (err: unknown) {
            console.error('Failed to update automation config:', err);
            throw err;
        } finally {
            setIsUpdating(false);
        }
    };

    const resetConfig = async () => {
        setIsUpdating(true);
        try {
            const response = await api.delete<AutomationConfig>('/sxm/config', {
                params: { userId }
            });
            setConfig(response.data);
            return response.data;
        } catch (err: unknown) {
            console.error('Failed to reset automation config:', err);
            throw err;
        } finally {
            setIsUpdating(false);
        }
    };

    useEffect(() => {
        fetchConfig();
    }, [fetchConfig]);

    return {
        config,
        loading,
        error,
        updateConfig,
        resetConfig,
        isUpdating,
        refetch: fetchConfig,
    };
}
